import axios, {AxiosResponse} from 'axios'
import {API_URL, ID, Response} from '../../../../../../_metronic/helpers'
import {
  Customer,
  CustomerBulkAction,
  CustomerBulkActionResult,
  CustomersQueryResponse,
} from './_models'
import {
  InvoiceUsagePage,
  readInvoiceUsagePage,
} from '../../../shared/invoice-usage/invoiceUsageModels'

const CUSTOMER_URL = `${API_URL}/customer`
const GET_CUSTOMERS_URL = `${API_URL}/customers/query`

const getCustomers = (query: string): Promise<CustomersQueryResponse> => {
  return axios
    .get(`${GET_CUSTOMERS_URL}?${query}`)
    .then((d: AxiosResponse<CustomersQueryResponse>) => d.data)
}

const getCustomerById = (id: ID): Promise<Customer | undefined> => {
  return axios
    .get(`${CUSTOMER_URL}/${id}`)
    .then((response: AxiosResponse<Response<Customer>>) => response.data)
    .then((response: Response<Customer>) => response.data)
}

const getCustomerVersions = (customerId: ID): Promise<Customer[]> => {
  return axios
    .get(`${API_URL}/customers/subcustomers/${customerId}`)
    .then((response: AxiosResponse<Customer[]>) => response.data || [])
}

const getCustomerInvoiceUsage = (
  customerId: ID,
  page: number,
  itemsPerPage: number
): Promise<InvoiceUsagePage> => {
  return axios
    .get(`${CUSTOMER_URL}/${customerId}/invoices`, {params: {page, items_per_page: itemsPerPage}})
    .then((response: AxiosResponse<Response<InvoiceUsagePage>>) =>
      readInvoiceUsagePage(response.data?.data, page, itemsPerPage)
    )
}

const createCustomer = (customer: Customer): Promise<Customer | undefined> => {
  return axios
    .put(CUSTOMER_URL, customer)
    .then((response: AxiosResponse<Response<Customer>>) => response.data)
    .then((response: Response<Customer>) => response.data)
}

const updateCustomer = (customer: Customer): Promise<Customer | undefined> => {
  return axios
    .post(`${CUSTOMER_URL}/${customer.id}`, customer)
    .then((response: AxiosResponse<Response<Customer>>) => response.data)
    .then((response: Response<Customer>) => response.data)
}

const deleteCustomer = (customerId: ID): Promise<void> => {
  return axios.post(`${CUSTOMER_URL}/${customerId}/delete`).then(() => {})
}

const archiveCustomer = (customerId: ID): Promise<Customer | undefined> => {
  return axios
    .post(`${CUSTOMER_URL}/${customerId}/archive`)
    .then((response: AxiosResponse<Response<Customer>>) => response.data)
    .then((response: Response<Customer>) => response.data)
}

/** An archived buyer back on the active list (backend 1.0.0.715); 409 when an active buyer has its identifier. */
const restoreCustomer = (customerId: ID): Promise<Customer | undefined> => {
  return axios
    .post(`${CUSTOMER_URL}/${customerId}/restore`)
    .then((response: AxiosResponse<Response<Customer>>) => response.data)
    .then((response: Response<Customer>) => response.data)
}

/** The list's filters as the server takes them for «همه‌ی خریداران این فهرست». */
export type CustomerBulkScope =
  | {all: false; ids: Array<ID>}
  | {
      all: true
      filter: {type?: number; usage?: number; status?: number; search?: string}
      /** The count the person saw; the server refuses when the filters now match another count. */
      expectedCount: number
    }

/** One action on the selected buyers — or every buyer the filters match — in one request, answered with what happened. */
const runCustomerBulkAction = (
  scope: CustomerBulkScope,
  action: CustomerBulkAction
): Promise<CustomerBulkActionResult> => {
  const body = scope.all
    ? {action, all: true, filter: scope.filter, expectedCount: scope.expectedCount}
    : {action, ids: scope.ids.filter((id) => id !== undefined && id !== null).map(Number)}
  return axios
    .post(`${API_URL}/customers/bulk-action`, body, {timeout: 300000})
    .then((response: AxiosResponse<Response<CustomerBulkActionResult>>) => response.data.data!)
}

/** The list as its filters and sort show it, as an Excel file. */
const downloadCustomersExcel = (query: string): Promise<AxiosResponse<Blob>> =>
  axios.get(`${API_URL}/customers/export?${query}`, {responseType: 'blob', timeout: 180000})

export {
  getCustomers,
  deleteCustomer,
  archiveCustomer,
  restoreCustomer,
  runCustomerBulkAction,
  downloadCustomersExcel,
  getCustomerById,
  getCustomerVersions,
  getCustomerInvoiceUsage,
  createCustomer,
  updateCustomer,
}
